import { useId, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, Mail, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineFieldInput } from "@/components/inline-field-group";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/loading-button";
import { formatApiErrorMessage } from "@/lib/api-error";
import { formatMoney } from "@/lib/utils";
import { DismissActionButton } from "./dismiss-action-button";
import {
  type RefundRequestDraft,
  type useResolveAction,
  useRecordRefundOutcome,
  useRefundRequestDraft,
  useSendRefundRequest,
} from "./hooks";
import { ComposeTextarea } from "@/components/compose-field";
import { EmailRecipientsField } from "@/components/email-recipients-field";
import { useEmailRecipients } from "@/components/use-email-recipients";

type Step = null | "ask" | "refund" | "credit";

function cleanAmount(value: string) {
  return value.replace(/[$,\s]/g, "");
}

/**
 * A cancelled PO we already paid for (USA-1471): ask the supplier for the money back, or record that it
 * already came back as a refund or as a credit on our account. Sending the request closes the item; what
 * the supplier then owes waits on the PO's Waiting-on list.
 */
export function RefundOrCreditChoices({
  poId,
  actionId,
  askAmount,
  supplier,
  resolveAction,
  disabled,
}: {
  poId: string;
  actionId: number;
  askAmount: string | null;
  supplier: string;
  resolveAction: ReturnType<typeof useResolveAction>;
  disabled?: boolean;
}) {
  const id = useId();
  const draft = useRefundRequestDraft(poId);
  const send = useSendRefundRequest(poId);
  const outcome = useRecordRefundOutcome(poId);
  const [step, setStep] = useState<Step>(null);
  const [amount, setAmount] = useState(askAmount ?? "");
  const [email, setEmail] = useState<(RefundRequestDraft & { body: string }) | null>(null);
  // The amount the drafted text names now.
  const [written, setWritten] = useState<string | null>(null);
  const recipients = useEmailRecipients(
    email?.to_email ? { to: [email.to_email], cc: email.cc_emails ?? [] } : undefined,
  );
  const pending = draft.isPending || send.isPending || outcome.isPending || resolveAction.isPending;
  const amountValid = Number(amount) > 0;

  const openAsk = () =>
    draft.mutate(
      { actionId },
      {
        onSuccess: (result) => {
          setAmount(String(result.ask_amount));
          setWritten(String(result.ask_amount));
          setEmail({ ...result, body: result.body_text });
          setStep("ask");
        },
        onError: (error) => toast.error(formatApiErrorMessage("Request not drafted", error)),
      },
    );

  // The draft names the amount; a changed amount changes it in the text too.
  const changeAmount = (next: string) => {
    const value = cleanAmount(next);
    if (email && written && Number(value) > 0) {
      setEmail({ ...email, body: email.body.split(formatMoney(written)).join(formatMoney(value)) });
      setWritten(value);
    }
    setAmount(value);
  };

  const back = () => {
    setStep(null);
    setEmail(null);
    setWritten(null);
    setAmount(askAmount ?? "");
    recipients.reset();
  };

  const amountField = (
    <div className="space-y-1 sm:w-40">
      <Label htmlFor={`${id}-amount`}>{step === "ask" ? "Ask for" : step === "refund" ? "Refunded" : "Credit"}</Label>
      <InlineFieldInput
        id={`${id}-amount`}
        inputMode="decimal"
        value={amount}
        onChange={(event) => changeAmount(event.target.value)}
        aria-invalid={amount !== "" && !amountValid}
        className="tabular-nums"
      />
    </div>
  );

  if (step === "ask" && email) {
    return (
      <section aria-label="Refund or credit request" className="space-y-3 border-t pt-3">
        <div className="space-y-1">
          <EmailRecipientsField state={recipients} toName={email.to_name} disabled={send.isPending} />
          <p className="text-xs text-muted-foreground">
            {email.accounting_contact ? "Accounting contact, new conversation" : "On the PO conversation"}
          </p>
        </div>
        {amountField}
        <div className="space-y-1">
          <Label htmlFor={`${id}-body`} className="sr-only">
            Request
          </Label>
          <ComposeTextarea
            id={`${id}-body`}
            value={email.body}
            onChange={(event) => setEmail({ ...email, body: event.target.value })}
            rows={10}
            className="min-h-[180px]"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <LoadingButton
            size="sm"
            loading={send.isPending}
            loadingText="Sending..."
            disabled={!email.body.trim() || !amountValid || pending}
            onClick={() =>
              send.mutate(
                { actionId, amount, bodyText: email.body.trim(), recipients: recipients.override },
                {
                  onSuccess: () => toast.success(`Request sent · ${supplier} owes ${formatMoney(amount)} back`),
                  onError: (error) =>
                    toast.error(formatApiErrorMessage("Request not sent", error), {
                      description: "Your draft is still here. Try again.",
                    }),
                },
              )
            }
          >
            <Send className="size-3.5" aria-hidden="true" /> Send request
          </LoadingButton>
          <Button size="sm" variant="ghost" disabled={send.isPending} onClick={back}>
            <ArrowLeft className="size-3.5" aria-hidden="true" /> Back
          </Button>
        </div>
      </section>
    );
  }

  if (step === "refund" || step === "credit") {
    const kind = step === "refund" ? "refund" : "credit";
    return (
      <form
        className="space-y-3 border-t pt-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!amountValid) return;
          outcome.mutate(
            { actionId, outcome: kind, amount },
            {
              onSuccess: () =>
                toast.success(
                  kind === "refund"
                    ? `${formatMoney(amount)} refund recorded`
                    : `${formatMoney(amount)} credit on ${supplier}'s account`,
                ),
              onError: (error) => toast.error(formatApiErrorMessage("Not recorded", error)),
            },
          );
        }}
      >
        <p className="text-sm text-foreground/80">
          {kind === "refund"
            ? `${supplier} sent the money back.`
            : `${supplier} keeps it as a credit on our account. It shows when we next pay them.`}
        </p>
        {amountField}
        <div className="flex flex-wrap gap-2">
          <LoadingButton
            type="submit"
            size="sm"
            loading={outcome.isPending}
            loadingText="Saving..."
            disabled={!amountValid || pending}
          >
            <Check className="size-3.5" aria-hidden="true" /> {kind === "refund" ? "Record refund" : "Record credit"}
          </LoadingButton>
          <Button type="button" size="sm" variant="ghost" disabled={outcome.isPending} onClick={back}>
            <ArrowLeft className="size-3.5" aria-hidden="true" /> Back
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-2 border-t pt-3">
      <p className="text-xs font-medium text-muted-foreground">Choose how to handle this</p>
      <div className="flex flex-wrap gap-2">
        <LoadingButton
          size="sm"
          loading={draft.isPending}
          loadingText="Drafting..."
          disabled={pending || disabled}
          onClick={openAsk}
        >
          <Mail className="size-3.5" aria-hidden="true" /> Ask for refund or credit
        </LoadingButton>
        <Button size="sm" variant="outline" disabled={pending || disabled} onClick={() => setStep("refund")}>
          Already refunded
        </Button>
        <Button size="sm" variant="outline" disabled={pending || disabled} onClick={() => setStep("credit")}>
          Already credited
        </Button>
        <DismissActionButton actionId={actionId} resolveAction={resolveAction} variant="ghost" disabled={pending || disabled}>
          <X className="size-3.5" aria-hidden="true" /> Nothing to get back
        </DismissActionButton>
      </div>
    </div>
  );
}
